/**
 * Theme orchestration for the web app.
 *
 * Mirrors `apps/extension/src/shared/theme.ts` — same three-mode model
 * (`light` / `dark` / `auto`), same `data-theme` attribute on
 * `<html>`, same media-query listener for `auto`. The token sheets in
 * `packages/client-ui/src/tokens/tokens-figma.css` (imported via
 * `apps/web/src/global.css`) define the `[data-theme="dark"]` block,
 * so wiring the attribute is enough to pick up dark colors everywhere
 * without per-component CSS work.
 *
 * Why a fresh copy and not a shared `@reflecto/client-core` export:
 * the extension's version touches `window.matchMedia` at module-load
 * time, which makes it unsafe to import from a service worker (no
 * `window`) and from a Node test environment (jsdom is fine, but
 * vitest's default node env isn't). Centralising would require
 * lazy-initialising the media query through a setup function the
 * caller must remember to call. A 30-line duplicate is the cheaper
 * trade-off until a third surface needs the same orchestration.
 */
import type { ReflectoStorage } from "@reflecto/client-core";

type ThemeValue = ReflectoStorage["theme"];

let currentSetting: ThemeValue = "auto";

// Lazy-resolve the media query so `import`-ing this module from a
// non-DOM context (the service worker bundle, the vitest node env)
// doesn't crash at parse time. Real callers always run in the page,
// where `window.matchMedia` exists.
let mediaQuery: MediaQueryList | null = null;
function getMediaQuery(): MediaQueryList | null {
  if (mediaQuery) return mediaQuery;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  // Safari < 14 lacks `addEventListener` on MediaQueryList — the
  // older `addListener` is the cross-browser fallback. We only use
  // the new API since the rest of the web app already requires
  // modern browsers (Service Worker module imports, Push API).
  mediaQuery.addEventListener("change", () => {
    if (currentSetting === "auto") resolveAndApply();
  });
  return mediaQuery;
}

function resolveAndApply(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (currentSetting === "auto") {
    const mq = getMediaQuery();
    root.setAttribute("data-theme", mq?.matches ? "dark" : "light");
  } else {
    root.setAttribute("data-theme", currentSetting);
  }
}

/**
 * Apply theme to `<html data-theme="...">`. Listens to the system
 * preference media query when `theme === "auto"`. Idempotent — call
 * on every storage change without de-duplication.
 */
export function applyTheme(theme: ThemeValue): void {
  currentSetting = theme;
  resolveAndApply();
}
